import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { dangNhapGoogle, theoDoiDangNhap } from '../lib/auth.js';
import { layDb } from '../lib/firebase.js';
import { TEMPLATES, SINH_SLUGS } from '../data/templates.js';
import { docLuotTaiMau, docTatCaMau, luuMau, taiTepMau, xoaMau } from '../lib/templates.js';
import {
  chuanHoaMau, chuanHoaSlug, ganTepMoi, ganTepTiengAnh, boTepTiengAnh, tachDong, dinhDangKichThuoc,
  NHOM_MAU, NGANH_MAU, DO_SAU, GIAI_DOAN_MAU, DINH_DANG, TRAN_KICH_THUOC,
} from '../lib/templateModel.js';

/**
 * /admin/mau — quản lý thư viện mẫu dạng tệp.
 *
 * Mẫu sinh từ code (src/data/templates.js) chỉ hiện để tham khảo, không sửa ở
 * đây. Mẫu dạng tệp: mỗi lần thay tệp là một phiên bản mới, tệp cũ được giữ.
 */
const EMPTY = chuanHoaMau({ nganh: ['chung'], giaiDoan: ['Pre-seed', 'Seed'], doSau: 'lite' });
const ACCEPT = Object.values(DINH_DANG).map((d) => d.contentType).join(',') + ',.xlsx,.docx,.pdf';

function useQuyenQuanTri() {
  const [s, setS] = useState({ user: null, role: '', checking: true });
  useEffect(() => theoDoiDangNhap(async (next) => {
    setS({ user: next, role: '', checking: true });
    let role = '';
    if (next) try {
      const db = await layDb();
      const { doc, getDoc } = await import('firebase/firestore/lite');
      const snap = await getDoc(doc(db, 'admins', next.uid));
      role = snap.exists() ? snap.data()?.role || '' : '';
    } catch { role = ''; }
    setS({ user: next, role, checking: false });
  }), []);
  return { ...s, allowed: ['admin', 'editor'].includes(s.role) };
}

function XoaMau({ slug, onDeleted, setMessage }) {
  const [mo, setMo] = useState(false); const [go, setGo] = useState(''); const [busy, setBusy] = useState(false);
  if (!mo) return <div className="admin-danger-toggle"><button type="button" className="text-button danger" onClick={() => setMo(true)}>Xoá mẫu này…</button></div>;
  async function xoa() {
    setBusy(true); setMessage('Đang xoá mẫu và các phiên bản tệp…');
    try { await xoaMau(slug); setMessage('Đã xoá. Trang tĩnh của mẫu sẽ được gỡ ở lần build kế tiếp.'); onDeleted(slug); }
    catch (e) { setMessage(e?.code === 'permission-denied' ? 'Tài khoản này không có quyền xoá (cần role admin).' : e.message); }
    finally { setBusy(false); }
  }
  return <div className="admin-danger">
    <p><strong>Xoá vĩnh viễn mẫu “{slug}” và mọi phiên bản tệp?</strong></p>
    <p className="nho">Liên kết tải trong bài viết cũ sẽ không còn hoạt động. Muốn tạm ẩn, hãy bấm “Lưu nháp”.</p>
    <label htmlFor="xoa-mau">Gõ lại đường dẫn <code>{slug}</code> để xác nhận</label>
    <input id="xoa-mau" type="text" value={go} autoComplete="off" onChange={(e) => setGo(e.target.value)} />
    <div className="admin-danger-actions">
      <button type="button" className="nut nut-nguy-hiem" disabled={go.trim() !== slug || busy} onClick={xoa}>{busy ? 'Đang xoá…' : 'Xoá vĩnh viễn'}</button>
      <button type="button" className="text-button" onClick={() => { setMo(false); setGo(''); }}>Huỷ</button>
    </div>
  </div>;
}

export default function TemplateAdmin() {
  const { user, role, allowed, checking } = useQuyenQuanTri();
  const [list, setList] = useState([]); const [draft, setDraft] = useState(EMPTY);
  const [message, setMessage] = useState(''); const [busy, setBusy] = useState(false);
  const [ghiChu, setGhiChu] = useState(''); const [luotTai, setLuotTai] = useState({});
  const daLuu = list.some((m) => m.slug === draft.slug);
  const slugNow = draft.slug || chuanHoaSlug(draft.title);

  async function refresh() { try { setList((await docTatCaMau()).sort((a, b) => a.title.localeCompare(b.title))); } catch (e) { setMessage(e.message); } }
  useEffect(() => {
    if (!allowed) return;
    refresh();
    docLuotTaiMau(2000).then((rows) => setLuotTai(Object.fromEntries(rows.map((r) => [r.slug, r.soLuot])))).catch(() => {});
  }, [allowed]);

  const set = (k, v) => setDraft((o) => ({ ...o, [k]: v }));
  const bat = (k, v, on) => setDraft((o) => ({ ...o, [k]: on ? [...new Set([...(o[k] || []), v])] : (o[k] || []).filter((x) => x !== v) }));
  const trungSinh = SINH_SLUGS.includes(slugNow);

  async function chonTep(file) {
    if (!file) return;
    if (!slugNow) { setMessage('Đặt tiêu đề (hoặc đường dẫn) trước khi tải tệp — đường dẫn quyết định nơi lưu tệp.'); return; }
    if (trungSinh) { setMessage(`Đường dẫn "${slugNow}" đã dùng cho một mẫu sinh từ code. Hãy đổi đường dẫn.`); return; }
    if (draft.fileUrl && !ghiChu.trim()) { setMessage('Đây là bản thay thế: ghi chú thay đổi của phiên bản mới trước khi chọn tệp.'); return; }
    setBusy(true); setMessage(`Đang tải ${file.name} (${dinhDangKichThuoc(file.size)})…`);
    try {
      const phienBan = (draft.phienBan || 0) + 1;
      const tep = await taiTepMau(file, slugNow, phienBan);
      setDraft((o) => ganTepMoi({ ...o, slug: slugNow }, tep, ghiChu || 'Bản đầu tiên'));
      setGhiChu('');
      setMessage(`Đã tải phiên bản ${phienBan}. Bấm “Lưu nháp” hoặc “Xuất bản” để ghi nhận.`);
    } catch (e) { setMessage(e?.code === 'storage/unauthorized' ? 'Không có quyền tải tệp (kiểm tra Storage Rules đã deploy chưa).' : e.message); }
    finally { setBusy(false); }
  }

  async function chonTepEn(file) {
    if (!file) return;
    if (!slugNow) { setMessage('Đặt tiêu đề (hoặc đường dẫn) trước khi tải tệp.'); return; }
    if (trungSinh) { setMessage(`Đường dẫn "${slugNow}" đã dùng cho một mẫu sinh từ code. Hãy đổi đường dẫn.`); return; }
    setBusy(true); setMessage(`Đang tải bản tiếng Anh ${file.name} (${dinhDangKichThuoc(file.size)})…`);
    try {
      const phienBan = (draft.fileEnPhienBan || 0) + 1;
      const tep = await taiTepMau(file, slugNow, phienBan, 'en');
      setDraft((o) => ganTepTiengAnh({ ...o, slug: slugNow }, tep));
      setMessage(`Đã tải bản tiếng Anh (EN v${phienBan}). Bấm “Lưu nháp” hoặc “Xuất bản” để ghi nhận.`);
    } catch (e) { setMessage(e?.code === 'storage/unauthorized' ? 'Không có quyền tải tệp (kiểm tra Storage Rules đã deploy chưa).' : e.message); }
    finally { setBusy(false); }
  }

  async function save(status) {
    setMessage('Đang lưu…');
    try {
      const saved = await luuMau({ ...draft, slug: slugNow }, status);
      setDraft(chuanHoaMau(saved));
      setMessage(status === 'PUBLISHED'
        ? 'Đã xuất bản. Trang /mau/… hiện ngay; chạy lại npm run deploy để có HTML tĩnh và vào sitemap.'
        : 'Đã lưu bản nháp.');
      refresh();
    } catch (e) { setMessage(e?.code === 'permission-denied' ? 'Firestore từ chối lưu (kiểm tra Firestore Rules đã deploy chưa).' : e.message); }
  }

  const tabsText = useMemo(() => (draft.tabs || []).map((t) => (t.moTa ? `${t.ten} | ${t.moTa}` : t.ten)).join('\n'), [draft.tabs]);

  if (checking) return <div className="surface section-space"><p>Đang kiểm tra quyền quản trị…</p></div>;
  if (!user) return <section className="surface admin-gate"><h1>Quản trị thư viện mẫu</h1><p>Đăng nhập bằng tài khoản đã được cấp quyền.</p><button className="nut" onClick={() => dangNhapGoogle()}>Đăng nhập với Google</button></section>;
  if (!allowed) return <section className="surface admin-gate"><h1>Chưa có quyền quản trị</h1><p>Tài khoản <strong>{user.email}</strong> chưa nằm trong danh sách admin.</p><p className="nho">UID để cấp quyền: <code>{user.uid}</code></p></section>;

  return <div className="admin-playbook">
    <header className="page-heading"><p className="eyebrow">Khu vực quản trị · <Link to="/admin/playbook">Playbook</Link> · Thư viện mẫu · <Link to="/admin/chuong-trinh">Chương trình</Link> · <Link to="/admin/nguon-von">Nguồn vốn</Link></p><h1>Thư viện mẫu</h1>
      <p>Tải lên mẫu Excel, Word hoặc PDF dựng sẵn. Mỗi lần thay tệp là một phiên bản mới; tệp cũ được giữ lại.</p></header>
    <div className="admin-layout">
      <aside className="surface admin-list">
        <div className="hang-nut"><button className="nut" onClick={() => { setDraft(EMPTY); setGhiChu(''); setMessage(''); }}>+ Mẫu mới</button></div>
        <h2>Mẫu dạng tệp</h2>
        {list.length === 0 && <p className="nho">Chưa có mẫu nào.</p>}
        {list.map((m) => <button className="admin-post-row" key={m.slug} onClick={() => { setDraft(m); setGhiChu(''); setMessage(''); }}>
          <strong>{m.title || m.slug}</strong>
          <span>{m.status === 'PUBLISHED' ? 'Đã xuất bản' : 'Bản nháp'} · v{m.phienBan || 0}{m.fileEnUrl ? ' · có EN' : ''} · {luotTai[m.slug] || 0} lượt tải{luotTai[`${m.slug}:en`] ? ` (+${luotTai[`${m.slug}:en`]} EN)` : ''}</span>
        </button>)}
        <h2>Sinh từ code</h2>
        <p className="nho">Sửa trong <code>src/data/templates.js</code>.</p>
        {TEMPLATES.filter((t) => t.kind === 'sinh').map((t) => <p className="nho" key={t.slug}>{t.title} · {luotTai[t.slug] || 0} lượt tải{luotTai[`${t.slug}:en`] ? ` (+${luotTai[`${t.slug}:en`]} EN)` : ''}</p>)}
      </aside>
      <section className="surface admin-editor">
        <div className="o-nhap admin-fields">
          <div className="o-rong"><label>Tiêu đề</label><input type="text" value={draft.title} placeholder="Mô hình tài chính 36 tháng đủ 3 báo cáo — bản SaaS" onChange={(e) => set('title', e.target.value)} /></div>
          <div><label>Đường dẫn {draft.fileUrl && <span className="nho">(đã khoá vì đã có tệp)</span>}</label>
            <input type="text" value={draft.slug} placeholder={chuanHoaSlug(draft.title)} disabled={Boolean(draft.fileUrl) || daLuu} onChange={(e) => set('slug', chuanHoaSlug(e.target.value))} />
            {trungSinh && <p className="story-loi">Trùng với mẫu sinh từ code.</p>}</div>
          <div><label>Mã mẫu</label><input type="text" value={draft.maMau} placeholder="FM-CORE-SAAS-L" onChange={(e) => set('maMau', e.target.value.toUpperCase())} /></div>
          <div className="o-rong"><label>Mô tả ngắn (hiện trên thẻ mẫu và Google)</label><textarea rows="3" value={draft.excerpt} onChange={(e) => set('excerpt', e.target.value)} /></div>
          <div><label>Nhóm tài liệu</label><select value={draft.nhom} onChange={(e) => set('nhom', e.target.value)}>{NHOM_MAU.filter((n) => n.slug !== 'dashboard').map((n) => <option key={n.slug} value={n.slug}>{n.label}</option>)}</select></div>
          <div><label>Độ sâu</label><select value={draft.doSau} onChange={(e) => set('doSau', e.target.value)}>{Object.entries(DO_SAU).map(([k, v]) => <option key={k} value={k}>{v.label} — {v.moTa}</option>)}</select></div>
          <div><label>Nhãn ngắn trên thẻ (thay cho "Lite")</label><input type="text" maxLength={40} value={draft.nhanNgan || ''} placeholder="Đầy đủ 3 báo cáo, 36 tháng" onChange={(e) => set('nhanNgan', e.target.value)} /></div>
          <div className="o-rong"><label>Ngành áp dụng</label><div className="admin-checks">{NGANH_MAU.map((n) => <label key={n.slug}><input type="checkbox" checked={draft.nganh.includes(n.slug)} onChange={(e) => bat('nganh', n.slug, e.target.checked)} /> {n.label}</label>)}</div></div>
          <div className="o-rong"><label>Giai đoạn</label><div className="admin-checks">{GIAI_DOAN_MAU.map((g) => <label key={g}><input type="checkbox" checked={draft.giaiDoan.includes(g)} onChange={(e) => bat('giaiDoan', g, e.target.checked)} /> {g}</label>)}</div></div>

          <div className="o-rong admin-file">
            <label>Tệp mẫu (.xlsx, .docx, .pdf · tối đa {dinhDangKichThuoc(TRAN_KICH_THUOC)})</label>
            {draft.fileUrl
              ? <p><strong>Phiên bản {draft.phienBan}</strong> · {draft.fileName} · {dinhDangKichThuoc(draft.fileSize)} · <a href={draft.fileUrl} target="_blank" rel="noopener noreferrer">tải thử</a>
                {draft.ghiChuPhienBan && <><br /><span className="nho">Ghi chú: {draft.ghiChuPhienBan}</span></>}</p>
              : <p className="nho">Chưa có tệp.</p>}
            {draft.fileUrl && <><label htmlFor="ghi-chu-pb">Ghi chú cho phiên bản mới (bắt buộc khi thay tệp)</label>
              <input id="ghi-chu-pb" type="text" value={ghiChu} placeholder="Ví dụ: sửa công thức thuế, thêm kịch bản xấu" onChange={(e) => setGhiChu(e.target.value)} /></>}
            <label className="admin-upload">
              <span className="nut phu">{busy ? 'Đang tải…' : draft.fileUrl ? 'Tải phiên bản mới' : 'Chọn tệp mẫu'}</span>
              <input type="file" accept={ACCEPT} disabled={busy} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; chonTep(f); }} />
            </label>
            {draft.lichSu?.length > 0 && <details><summary className="nho">Lịch sử {draft.lichSu.length} phiên bản trước</summary>
              <ul className="nho">{[...draft.lichSu].reverse().map((h) => <li key={h.phienBan + h.filePath}>v{h.phienBan} · {h.ngay} · {dinhDangKichThuoc(h.fileSize)}{h.ghiChu && ` · ${h.ghiChu}`}</li>)}</ul>
            </details>}
          </div>

          <div className="o-rong admin-file">
            <label>Bản tiếng Anh (không bắt buộc) — cùng cấu trúc và công thức, để gửi nhà đầu tư nước ngoài</label>
            {draft.fileEnUrl
              ? <p><strong>EN v{draft.fileEnPhienBan}</strong> · {draft.fileEnName} · {dinhDangKichThuoc(draft.fileEnSize)} · <a href={draft.fileEnUrl} target="_blank" rel="noopener noreferrer">tải thử</a>
                {' · '}<button type="button" className="text-button danger" onClick={() => setDraft((o) => boTepTiengAnh(o))}>Bỏ bản tiếng Anh</button></p>
              : <p className="nho">Chưa có bản tiếng Anh. Trang mẫu chỉ hiện nút tiếng Việt.</p>}
            <label className="admin-upload">
              <span className="nut phu">{busy ? 'Đang tải…' : draft.fileEnUrl ? 'Thay bản tiếng Anh' : 'Chọn tệp tiếng Anh'}</span>
              <input type="file" accept={ACCEPT} disabled={busy} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; chonTepEn(f); }} />
            </label>
          </div>

          <div className="o-rong"><label>Các sheet / phần trong tệp — mỗi dòng: Tên | mô tả</label>
            <textarea rows="6" defaultValue={tabsText} key={`tabs-${draft.slug}-${draft.phienBan}`} placeholder={'Dashboard | Chỉ số gọi vốn, tổng hợp 3 năm, biểu đồ\nInputs | Giả định chung, kế hoạch gọi vốn'} onBlur={(e) => set('tabs', tachDong(e.target.value))} /></div>
          <div className="o-rong"><label>Số liệu cần chuẩn bị — mỗi dòng một ý</label>
            <textarea rows="4" value={(draft.bienSo || []).join('\n')} onChange={(e) => set('bienSo', e.target.value.split('\n'))} /></div>
          <div className="o-rong"><label>Lưu ý khi dùng mẫu — mỗi dòng một ý</label>
            <textarea rows="4" value={(draft.luuY || []).join('\n')} onChange={(e) => set('luuY', e.target.value.split('\n'))} /></div>
          <div><label>Bài Playbook liên quan (slug)</label><input type="text" value={draft.baiLienQuan} onChange={(e) => set('baiLienQuan', e.target.value)} /></div>
          <div><label>Công cụ liên quan — mỗi dòng: slug | Tên nút</label>
            <textarea rows="2" value={(draft.congCu || []).map((c) => `${c.slug} | ${c.label}`).join('\n')} onChange={(e) => set('congCu', tachDong(e.target.value, ['slug', 'label']))} /></div>
        </div>
        <div className="admin-actions">
          <button className="nut phu" disabled={busy} onClick={() => save('DRAFT')}>Lưu nháp</button>
          <button className="nut" disabled={busy} onClick={() => save('PUBLISHED')}>Xuất bản</button>
          {draft.status === 'PUBLISHED' && daLuu && <Link className="nut phu" to={`/mau/${draft.slug}`} target="_blank">Xem trang mẫu</Link>}
        </div>
        <p role="status">{message}</p>
        {role === 'admin' && daLuu && <XoaMau key={draft.slug} slug={draft.slug} setMessage={setMessage}
          onDeleted={(s) => { setList((o) => o.filter((m) => m.slug !== s)); setDraft(EMPTY); }} />}
      </section>
    </div>
  </div>;
}
